import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Camera, Loader2, Sparkles, Check, Target, Lightbulb, AlertCircle, Info } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import { imageToBase64 } from '../../lib/imageToBase64'
import ProgressAlbum from './components/ProgressAlbum'
import { cn } from '../../lib/utils'

export function PhysiqueCheckPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user } = useAuth()

  const fileRef = useRef(null)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const [history, setHistory] = useState([])

  useEffect(() => {
    if (!user) return
    let alive = true
    ;(async () => {
      const { data } = await supabase
        .from('physique_analyses')
        .select('id, analysis, created_at')
        .order('created_at', { ascending: false })
        .limit(10)
      if (alive) setHistory(data || [])
    })()
    return () => { alive = false }
  }, [user, result])

  const handlePick = () => fileRef.current?.click()

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || busy) return
    setError(null)
    setResult(null)
    setBusy(true)
    try {
      const { base64, mime, dataUrl } = await imageToBase64(file)
      setPreview(dataUrl)
      const resp = await supabase.functions.invoke('analyze-body-photo', {
        body: { image_base64: base64, mime },
      })
      if (resp.error) {
        setError(t('physique.failed'))
      } else if (resp.data?.analysis?.notPerson) {
        setError(t('physique.notPerson'))
      } else {
        setResult(resp.data.analysis)
      }
    } catch {
      setError(t('physique.failed'))
    } finally {
      setBusy(false)
    }
  }

  const dateLabel = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', { dateStyle: 'medium' })
    } catch { return '' }
  }

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('physique.title')}
        </h1>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
      />

      <Card>
        <CardContent className="p-5 flex flex-col items-center gap-3 text-center">
          {preview ? (
            <img src={preview} alt="" className="rounded-xl max-h-56 w-auto object-cover" />
          ) : (
            <div className="w-full py-8 flex flex-col items-center gap-2 text-muted-foreground">
              <Sparkles className="w-8 h-8 text-app-primary-500" />
              <p className="text-sm" dir="auto">{t('physique.intro')}</p>
            </div>
          )}
          <button
            type="button"
            onClick={handlePick}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
            dir="auto"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {busy ? t('physique.analyzing') : (preview ? t('physique.tryAnother') : t('physique.pick'))}
          </button>
          <p className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" dir="auto">
            <Info className="w-3 h-3 flex-shrink-0" /> {t('physique.privacy')}
          </p>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="p-4 flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400" dir="auto">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </CardContent>
        </Card>
      )}

      {result && <AnalysisCard analysis={result} t={t} />}

      {result && (
        <p className="text-[11px] text-muted-foreground text-center px-4" dir="auto">
          {t('physique.disclaimer')}
        </p>
      )}

      <ProgressAlbum />

      {history.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-1" dir="auto">{t('physique.history')}</p>
          {history.map((h) => (
            <details key={h.id} className="group">
              <summary className="cursor-pointer list-none">
                <Card className="hover:bg-accent transition-colors">
                  <CardContent className="p-3 flex items-center justify-between gap-2">
                    <span className="text-sm text-foreground truncate" dir="auto">{h.analysis?.overall || t('physique.title')}</span>
                    <span className="text-[11px] text-muted-foreground flex-shrink-0" dir="auto">{dateLabel(h.created_at)}</span>
                  </CardContent>
                </Card>
              </summary>
              <div className="mt-2">
                <AnalysisCard analysis={h.analysis} t={t} compact />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  )
}

function AnalysisCard({ analysis, t, compact }) {
  return (
    <Card>
      <CardContent className={cn('space-y-3', compact ? 'p-4' : 'p-5')}>
        {analysis.overall && (
          <p className="text-sm text-foreground leading-relaxed" dir="auto">{analysis.overall}</p>
        )}
        {analysis.strengths?.length > 0 && (
          <Section icon={Check} tone="text-emerald-500" title={t('physique.strengths')} items={analysis.strengths} />
        )}
        {analysis.focusAreas?.length > 0 && (
          <Section icon={Target} tone="text-app-primary-500" title={t('physique.focusAreas')} items={analysis.focusAreas} />
        )}
        {analysis.tips?.length > 0 && (
          <Section icon={Lightbulb} tone="text-amber-500" title={t('physique.tips')} items={analysis.tips} />
        )}
      </CardContent>
    </Card>
  )
}

function Section({ icon: Icon, tone, title, items }) {
  return (
    <div>
      <p className={cn('flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-1', tone)} dir="auto">
        <Icon className="w-3.5 h-3.5" /> {title}
      </p>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-foreground/90 flex gap-1.5" dir="auto">
            <span className={tone}>•</span><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PhysiqueCheckPage
